/*
Context:
  getUser,
  checkUserValidity,
  updateUser,
  et toutes méthodes de manipulation d'un user existant en DB
*/

import { User } from "./userModel";
import {
  getAllUsersRequest,
  getCustomerPortalUserLinkRequest,
  getUserPremiumLinkRequest,
  getUserRequest,
  updateUserRequest,
} from "./userApi";
import { toastsWithIntl } from "../toastService";
import { authService } from "../auth/authService";
import * as Sentry from "@sentry/react";
import { getOrganizationRequest } from "../organization/organizationApi";
import { isFreeOrganizationAccount } from "../organization/organizationModel";

interface UserService {
  getLoggedUser: () => Promise<User>;
  /** Post-login landing: free pro accounts go straight to their candidates */
  getLoggedUserHomeUrl: () => Promise<string>;
  getAllUsers: () => Promise<User[]>;
  getPremiumUserLink: (userId: User["id"]) => Promise<{ url: string }>;
  getCustomerPortalUserLink: (userId: User["id"]) => Promise<{ url: string }>;
  updateUserById: (
    userId: User["id"],
    user: Partial<
      Pick<
        User,
        | "firstname"
        | "lastname"
        | "organizationMembershipStatus"
        | "adminOrga"
        | "phoneNumber"
        | "positionInOrganization"
        | "usage"
      >
    >,
  ) => Promise<User>;
  updateUserPassword: (oldPassword: string, password: string) => Promise<User>;
}
export const userService = () => {
  const { getLoggedUserId } = authService();
  const { toastSuccess, toastError } = toastsWithIntl(["accounts"]);

  const getLoggedUserHomeUrl: UserService["getLoggedUserHomeUrl"] =
    async () => {
      const loggedUser = await getLoggedUser();
      if (!loggedUser.organizationId) return "/candidate";
      const organization = await getOrganizationRequest(
        loggedUser.organizationId,
      );
      return isFreeOrganizationAccount(organization, loggedUser)
        ? "/organization/candidates"
        : "/organization";
    };

  const getLoggedUser: UserService["getLoggedUser"] = () => {
    const loggedUserId = getLoggedUserId();
    if (!loggedUserId) return Promise.reject("not logged in");
    return getUserRequest(loggedUserId).then((u) => {
      Sentry.setUser({
        id: u.id,
        username: `${u.firstname} ${u.lastname}`,
        email: u.email,
      });

      return u;
    });
  };

  const updateUserById: UserService["updateUserById"] = (userId, user) => {
    return updateUserRequest(userId, user).then(
      (updateUser) => {
        toastSuccess("accounts:update.SUCCESS");
        return Promise.resolve(updateUser);
      },
      (err) => {
        toastError("accounts:update.ERROR");
        return Promise.reject(err);
      },
    );
  };

  const getPremiumUserLink: UserService["getPremiumUserLink"] = async (
    userId,
  ) => {
    const res = await getUserPremiumLinkRequest(userId);
    if (!res) throw new Error("Erreur lors de la récupération du lien premium");
    return res; // { url: "..." }
  };

  const getCustomerPortalUserLink: UserService["getCustomerPortalUserLink"] =
    async (userId) => {
      const res = await getCustomerPortalUserLinkRequest(userId);
      if (!res)
        throw new Error("Erreur lors de la récupération du lien premium");
      return res; // { url: "..." }
    };

  return {
    getLoggedUser,
    getLoggedUserHomeUrl,
    getAllUsers: getAllUsersRequest,
    updateUserById,
    getPremiumUserLink,
    getCustomerPortalUserLink,
  };
};
